import {createAsyncThunk} from '@reduxjs/toolkit'
import AxiosClient from '../AxiosClient'
import AxiosClientForFile from '../AxiosClientForFile'
import {OMS_BASE} from 'src/utils/constants'

// GET /oms/generate-sales-order-number?type=sales_order_number
export const generateOmsSalesOrderNumberAsync = createAsyncThunk(
  'omsOrders/generateOmsSalesOrderNumberAsync',
  async function (_: void, toolkit) {
    return await AxiosClient(
      'GET',
      `${OMS_BASE}/generate-sales-order-number`,
      [],
      toolkit,
      {type: 'sales_order_number'},
      toolkit.signal
    )
  }
)

// POST /oms/orders?is_confirmed=false
export const createOmsOrderAsync = createAsyncThunk(
  'omsOrders/createOmsOrderAsync',
  async function (payload: {data: any; is_confirmed?: boolean}, toolkit) {
    return await AxiosClient(
      'POST',
      `${OMS_BASE}/orders`,
      payload.data,
      toolkit,
      {is_confirmed: payload.is_confirmed ?? false}
    )
  }
)

// GET /oms/orders/{order_id}
export const getOmsOrderAsync = createAsyncThunk(
  'omsOrders/getOmsOrderAsync',
  async (payload: {orderId: number | string; query?: Record<string, any>}, toolkit) => {
    return await AxiosClient(
      'GET',
      `${OMS_BASE}/orders/${payload.orderId}`,
      [],
      toolkit,
      payload.query ?? [],
      toolkit.signal
    )
  }
)

// PUT /oms/orders/{order_id}
export const updateOmsOrderAsync = createAsyncThunk(
  'omsOrders/updateOmsOrderAsync',
  async (payload: {orderId: number | string; data: any}, toolkit) => {
    return await AxiosClient(
      'PUT',
      `${OMS_BASE}/orders/${payload.orderId}`,
      payload.data,
      toolkit,
      []
    )
  }
)

// PUT /oms/orders/{order_id}/allocation
export const updateOmsOrderAllocationAsync = createAsyncThunk(
  'omsOrders/updateOmsOrderAllocationAsync',
  async (payload: {orderId: number | string; data: any}, toolkit) => {
    return await AxiosClient(
      'PUT',
      `${OMS_BASE}/orders/${payload.orderId}/allocation`,
      payload.data,
      toolkit,
      []
    )
  }
)

// ─── Order Custom Fields ──────────────────────────────────────────────────────

// POST /oms/orders/{order_id}/custom-fields  — create / update (bulk array payload)
export const saveOmsOrderCustomFieldsAsync = createAsyncThunk(
  'omsOrders/saveOmsOrderCustomFieldsAsync',
  async function (payload: {orderId: number | string; data: any[]}, toolkit) {
    return await AxiosClient(
      'POST',
      `${OMS_BASE}/orders/${payload.orderId}/custom-fields`,
      payload.data,
      toolkit,
      []
    )
  }
)

// GET /oms/orders/{order_id}/custom-fields  — list of saved custom fields for an order
export const getOmsOrderCustomFieldsAsync = createAsyncThunk(
  'omsOrders/getOmsOrderCustomFieldsAsync',
  async function (
    payload: {
      orderId: number | string
      page?: number
      limit?: number
      sort_by?: string
      order_by?: string
    },
    toolkit
  ) {
    return await AxiosClient(
      'GET',
      `${OMS_BASE}/orders/${payload.orderId}/custom-fields`,
      [],
      toolkit,
      {
        page: payload.page ?? 1,
        limit: payload.limit ?? 100,
        sort_by: payload.sort_by ?? 'asc',
        order_by: payload.order_by ?? 'id',
      },
      toolkit.signal
    )
  }
)

// GET /oms/orders/{order_id}/custom-fields/{custom_field_id}
export const getOmsOrderCustomFieldByIdAsync = createAsyncThunk(
  'omsOrders/getOmsOrderCustomFieldByIdAsync',
  async function (payload: {orderId: number | string; customFieldId: number | string}, toolkit) {
    return await AxiosClient(
      'GET',
      `${OMS_BASE}/orders/${payload.orderId}/custom-fields/${payload.customFieldId}`,
      [],
      toolkit,
      []
    )
  }
)

// DELETE /oms/orders/{order_id}/custom-fields/{custom_field_id}
export const deleteOmsOrderCustomFieldAsync = createAsyncThunk(
  'omsOrders/deleteOmsOrderCustomFieldAsync',
  async function (payload: {orderId: number | string; customFieldId: number | string}, toolkit) {
    return await AxiosClient(
      'DELETE',
      `${OMS_BASE}/orders/${payload.orderId}/custom-fields/${payload.customFieldId}`,
      [],
      toolkit,
      []
    )
  }
)

// POST /oms/orders/{orderId}/invoice
export const createOmsOrderInvoiceAsync = createAsyncThunk(
  'omsOrders/createOmsOrderInvoiceAsync',
  async (payload: { orderId: number | string }, toolkit) => {
    return await AxiosClient(
      'POST',
      `${OMS_BASE}/orders/${payload.orderId}/invoice`,
      {}, // no body
      toolkit,
      []
    )
  }
)

// GET /api/order-invoice-download?order_ids=[{orderId}]
export const downloadOmsOrderInvoiceAsync = createAsyncThunk(
  'omsOrders/downloadOmsOrderInvoiceAsync',
  async (payload: { orderId: number | string }, toolkit) => {
    const response = await AxiosClient(
      'GET',
      `/api/order-invoice-download?order_ids=[${payload.orderId}]`,
      [],
      toolkit,
      [],
      toolkit.signal,
      'blob'
    )
    
    const blob = new Blob([response.data], { type: "application/pdf" })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `invoice-${payload.orderId}.pdf`

    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)

    return true
  }
)

// ─── Orders CSV Upload ────────────────────────────────────────────────────────

// GET /oms/v1/list-uploaded-order-file-info?platform_id=&page=&limit=
export const getUploadDataOrderAsync = createAsyncThunk(
  'orders/getUploadData',
  async (payload: {id: number; pag?: number; limit?: number}, toolkit) => {
    const url = `${OMS_BASE}/v1/list-uploaded-order-file-info?platform_id=${payload.id}&page=${payload.pag || 1}&limit=${payload.limit || 10}`
    return await AxiosClient('GET', url, [], toolkit, [])
  }
)

// POST /oms/v1/upload-order-file  FormData: platform_id, file
export const uploadOrderCsvAsync = createAsyncThunk(
  'orders/uploadOrderCsv',
  async (formData: FormData, toolkit) => {
    return await AxiosClientForFile('POST', `${OMS_BASE}/v1/upload-order-file`, formData, toolkit, {})
  }
)
